import { mergeCheckedIdsToParent } from '@framework/ui/Organisms/Filters/helpers';

import {
  findMatchingPreset,
  getSingleFaceRangeValue,
  isFromPreset,
  getSubsectorsFromSectors,
  buildRange,
  Filters,
  Payload,
  prepareIncludedCurrentFaces,
  Range,
} from './utils';
import { IBondsFilterState } from '@/creditflowresearch/domain/vo/Bond/BondsFiltersContextData';

export const getInitialState = () => ({
  dateFrom: null,
  dateTo: null,
  includedStatus: 'all',
  includedSubsectorIds: [],
  includedIssuerIds: [],
  includedDealerIds: [],
  includedBenchmarkGroupIds: [],
  includedSpeedTypeIds: [],
  includedRatingGroupIds: [],
  includedRatingAgencyIds: [],
  currentFaceRange: {
    range: {
      min: null,
      max: null,
    },
    minValue: null,
    maxValue: null,
  },
  currentFaceRangeIds: [],
  walRange: {
    range: {
      min: null,
      max: null,
    },
    minValue: null,
    maxValue: null,
  },
  spreadRange: {
    range: {
      min: null,
      max: null,
    },
    minValue: null,
    maxValue: null,
  },
  speedRange: {
    range: {
      min: null,
      max: null,
    },
    minValue: null,
    maxValue: null,
  },
  excludedSectorIds: [],
  includedDealIds: [],
  includedIndustryIds: [],
  includedParentIds: [],
  includedSubindustryIds: [],
  includedSectorIds: [],
  includedCurrentFaces: [],
  includedWalFrom: null,
  includedWalTo: null,
  includedSpreadFrom: null,
  includedSpreadTo: null,
  includedSpeedFrom: null,
  includedSpeedTo: null,
});

export const getFiltersPayload = (filters: Filters = {}, checkboxes: any) => {
  const {
    currentFaceRange,
    walRange,
    spreadRange,
    speedRange,
    ...staticFilterFields
  } = filters;

  const rangesPayload = mapRangesForPayload(filters);

  const sectorIds = mergeCheckedIdsToParent(
    checkboxes,
    staticFilterFields,
    'sectorList',
    'includedSubsectorIds',
  );

  return {
    includedAssetClassId: null,
    ...staticFilterFields,
    includedSectorIds: sectorIds.parentIds,
    includedSubsectorIds: sectorIds.childrenIds,
    ...rangesPayload,
  };
};

export const mapRangesForFilters = (bondsFilter) => ({
  currentFaceRange: {
    range: bondsFilter.currentFaceRange.range,
    minValue: bondsFilter.currentFaceRange.range.min,
    maxValue: bondsFilter.currentFaceRange.range.max,
  },
  walRange: {
    range: bondsFilter.walRange.range,
    minValue: bondsFilter.walRange.range.min,
    maxValue: bondsFilter.walRange.range.max,
  },
  spreadRange: {
    range: bondsFilter.spreadRange.range,
    minValue: bondsFilter.spreadRange.range.min,
    maxValue: bondsFilter.spreadRange.range.max,
  },
  speedRange: {
    range: bondsFilter.speedRange.range,
    minValue: bondsFilter.speedRange.range.min,
    maxValue: bondsFilter.speedRange.range.max,
  },
});

export const mapRangesForPayload = (filters: Filters) => ({
  includedCurrentFaces: prepareIncludedCurrentFaces(filters),
  includedWalFrom: filters.walRange.range.min,
  includedWalTo: filters.walRange.range.max,
  includedSpreadFrom: filters.spreadRange.range.min,
  includedSpreadTo: filters.spreadRange.range.max,
  includedSpeedFrom: filters.speedRange.range.min,
  includedSpeedTo: filters.speedRange.range.max,
});

export const mapPayloadToRanges = (payload: Payload, fallbackFilters: Filters = {}) => ({
  currentFaceRange: buildRange(
    getSingleFaceRangeValue(payload.includedCurrentFaces, 'includedCurrentFaceFrom'),
    getSingleFaceRangeValue(payload.includedCurrentFaces, 'includedCurrentFaceTo'),
    fallbackFilters.currentFaceRange?.range,
  ),
  walRange: buildRange(
    payload.includedWalFrom,
    payload.includedWalTo,
    fallbackFilters.walRange?.range,
  ),
  spreadRange: buildRange(
    payload.includedSpreadFrom,
    payload.includedSpreadTo,
    fallbackFilters.spreadRange?.range,
  ),
  speedRange: buildRange(
    payload.includedSpeedFrom,
    payload.includedSpeedTo,
    fallbackFilters.speedRange?.range,
  ),
});

export const mapHashPayloadToFilters = (payload: Payload, bondsFilter: any = {}) => {
  const isBondsFilter = !!Object.keys(bondsFilter).length;

  let currentFaceRangeIds = [];

  const {
    includedCurrentFaces = [],
    includedSectorIds = [],
    ...othersFilters
  } = payload;

  let includedSubsectorIds: string[] = [...(othersFilters.includedSubsectorIds || [])];

  if (isBondsFilter) {
    if (includedSectorIds.length) {
      includedSubsectorIds = getSubsectorsFromSectors(includedSectorIds, bondsFilter.sectorList);
    }

    if (isFromPreset(includedCurrentFaces)) {
      includedCurrentFaces.forEach((face) => {
        const match = findMatchingPreset(face);
        if (match) currentFaceRangeIds.push(match.currentFaceRangeId);
      });
    }
  }

  const filtersToCleanupKeys = [
    'includedAssetClassId',
    'includedSectorIds',
    'includedCurrentFaces',
    'includedWalFrom',
    'includedWalTo',
    'includedSpreadFrom',
    'includedSpreadTo',
    'includedSpeedFrom',
    'includedSpeedTo',
  ];
  filtersToCleanupKeys.forEach((key) => delete othersFilters[key]);

  return {
    ...getInitialState(),
    ...othersFilters,
    includedSubsectorIds,
    currentFaceRangeIds,
    ...mapPayloadToRanges(payload, bondsFilter),
  };
};

export const extractRangeAndBaseFilters = (
  filters: IBondsFilterState,
  rangeKeys: string[],
  bondsFilter: IBondsFilterState,
) => {
  return Object.entries(filters).reduce(
    ([rangeFilters, otherFilters], [key, value]: [string, Range]) => {
      if (!rangeKeys.includes(key)) {
        return [rangeFilters, { ...otherFilters, [key]: value }];
      }
      const hasBondsFilter = Object.keys(bondsFilter).length > 0;

      const bondRange = bondsFilter[key]?.range || {};
      const { range = { min: null, max: null } } = value || {};

      const normalizedRange = {
        min: hasBondsFilter && range.min === bondRange.min ? null : range.min,
        max: hasBondsFilter && range.max === bondRange.max ? null : range.max,
      };

      return [
        { ...rangeFilters, [key]: { range: normalizedRange } },
        otherFilters,
      ];
    },
    [{}, {}],
  );
};
